<template>
  <div ref="wrap" class="chart">
    <canvas ref="canvas"></canvas>
    <div v-if="candles.length === 0" class="empty">No trades</div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { formatTokenPriceSol } from './utils'
import { fetchJson } from '@/stores/api/http'

const props = defineProps({
  entity: { type: Object, default: null },
  trades: { type: Array, default: () => [] },
})

const wrap = ref(null)
const canvas = ref(null)
let resizeObserver = null
let sampleTimer = null

const samples = ref([])
const history = ref([])
const historyMint = ref('')
const currentMint = computed(() => String(props.entity?.mint || ''))
const currentPrice = computed(() => poolPrice(props.entity))
const candles = computed(() => buildCandles([...history.value, ...props.trades], samples.value, currentPrice.value))

function priceOf(trade) {
  const amount = Number(trade?.amount || 0)
  const lamports = Number(trade?.lamports || 0)
  if (!Number.isFinite(amount) || amount <= 0) return 0
  if (!Number.isFinite(lamports) || lamports <= 0) return 0
  return lamports / amount
}

function timeOf(trade, fallback) {
  const parsed = Date.parse(String(trade?.created_at || ''))
  if (Number.isFinite(parsed)) return parsed
  return fallback
}

function poolPrice(entity) {
  const amount = Number(entity?.amount || 0)
  const lamports = Number(entity?.lamports || 0)
  if (!Number.isFinite(amount) || amount <= 0) return 0
  if (!Number.isFinite(lamports) || lamports <= 0) return 0
  return lamports / amount
}

function addSample() {
  const mint = currentMint.value
  const price = currentPrice.value
  if (!mint || !Number.isFinite(price) || price <= 0) return

  const now = Date.now()
  const prev = samples.value.at(-1)
  if (prev && prev.mint === mint && Math.abs(prev.price - price) < Number.EPSILON && now - prev.time < 900) {
    return
  }

  samples.value = [
    ...samples.value.filter(point => point.mint === mint).slice(-240),
    { mint, time: now, price },
  ]
}

function buildCandles(rows, liveSamples, currentPrice) {
  const trades = Array.isArray(rows) ? rows : []
  const tradePoints = trades
    .map((trade, index) => ({
      time: timeOf(trade, index * 1000),
      price: priceOf(trade),
      side: String(trade?.method || ''),
    }))
    .filter(point => Number.isFinite(point.price) && point.price > 0)

  const samplePoints = (Array.isArray(liveSamples) ? liveSamples : [])
    .map(point => ({
      time: Number(point?.time || 0),
      price: Number(point?.price || 0),
      side: 'pool',
    }))
    .filter(point => Number.isFinite(point.time) && point.time > 0 && Number.isFinite(point.price) && point.price > 0)

  const points = [...tradePoints, ...samplePoints].sort((a, b) => a.time - b.time)

  if (Number.isFinite(currentPrice) && currentPrice > 0) {
    points.push({
      time: Date.now(),
      price: currentPrice,
      side: 'pool',
    })
  }

  if (!points.length) return []

  const bucketMs = 5000
  const out = []

  for (const point of points) {
    const time = Math.floor(point.time / bucketMs) * bucketMs
    let candle = out.at(-1)
    if (!candle || candle.time !== time) {
      candle = {
        time,
        open: point.price,
        high: point.price,
        low: point.price,
        close: point.price,
        volume: 1,
      }
      out.push(candle)
      continue
    }

    candle.high = Math.max(candle.high, point.price)
    candle.low = Math.min(candle.low, point.price)
    candle.close = point.price
    candle.volume++
  }

  return out.slice(-120)
}

function draw() {
  const el = canvas.value
  const host = wrap.value
  if (!el || !host) return

  const rect = host.getBoundingClientRect()
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
  const width = Math.max(1, Math.floor(rect.width))
  const height = Math.max(1, Math.floor(rect.height))
  el.width = Math.floor(width * dpr)
  el.height = Math.floor(height * dpr)
  el.style.width = `${width}px`
  el.style.height = `${height}px`

  const ctx = el.getContext('2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, height)

  const rows = candles.value
  const pad = { left: 10, right: 72, top: 12, bottom: 22 }
  const plotW = Math.max(1, width - pad.left - pad.right)
  const plotH = Math.max(1, height - pad.top - pad.bottom)

  ctx.fillStyle = '#0b0b0b'
  ctx.fillRect(0, 0, width, height)

  ctx.strokeStyle = '#1f2937'
  ctx.lineWidth = 1
  for (let i = 0; i < 4; i++) {
    const y = pad.top + (plotH / 3) * i
    ctx.beginPath()
    ctx.moveTo(pad.left, y)
    ctx.lineTo(width - pad.right, y)
    ctx.stroke()
  }

  if (!rows.length) return

  const targetVisible = rows.length < 18 ? 18 : 34
  const slotW = Math.max(7, Math.min(15, plotW / Math.min(targetVisible, Math.max(rows.length, 12))))
  const bodyW = Math.max(7, Math.min(13, slotW * 0.82))
  const visible = Math.max(12, Math.floor(plotW / slotW))
  const visibleRows = rows.slice(-visible)
  const values = visibleRows.flatMap(c => [c.high, c.low])
  let min = Math.min(...values)
  let max = Math.max(...values)
  if (min === max) {
    const padValue = Math.max(min * 0.08, Number.EPSILON)
    min -= padValue
    max += padValue
  }
  const range = max - min || 1
  const yOf = price => pad.top + (max - price) / range * plotH

  visibleRows.forEach((c, index) => {
    const x = width - pad.right - 4 - (visibleRows.length - index - 0.5) * slotW
    const open = yOf(c.open)
    const close = yOf(c.close)
    const high = yOf(c.high)
    const low = yOf(c.low)
    const up = c.close >= c.open
    const color = up ? '#22c55e' : '#ef4444'

    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.moveTo(x, high)
    ctx.lineTo(x, low)
    ctx.stroke()

    const rawTop = Math.min(open, close)
    const rawHeight = Math.abs(close - open)
    const h = Math.max(8, rawHeight)
    const top = rawHeight < 5 ? close - h / 2 : rawTop
    roundRect(ctx, x - bodyW / 2, top, bodyW, h, 2)
    ctx.fill()
  })

  ctx.fillStyle = '#9ca3af'
  ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, monospace'
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  const labelX = width - 8
  ctx.fillText(formatPrice(max), labelX, pad.top)
  ctx.fillText(formatPrice((max + min) / 2), labelX, pad.top + plotH / 2)
  ctx.fillText(formatPrice(min), labelX, pad.top + plotH)
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + r)
  ctx.lineTo(x + width, y + height - r)
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
  ctx.lineTo(x + r, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function formatPrice(v) {
  return formatTokenPriceSol(v)
}

onMounted(() => {
  resizeObserver = new ResizeObserver(draw)
  if (wrap.value) resizeObserver.observe(wrap.value)
  addSample()
  loadHistory()
  sampleTimer = window.setInterval(addSample, 1000)
  draw()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  if (sampleTimer) window.clearInterval(sampleTimer)
})

watch([currentMint, currentPrice], ([mint], [prevMint]) => {
  if (mint !== prevMint) {
    samples.value = []
    history.value = []
    loadHistory()
  }
  addSample()
})

watch(candles, draw, { deep: true })

async function loadHistory() {
  const mint = currentMint.value
  if (!mint || historyMint.value === mint) return
  historyMint.value = mint
  try {
    const qs = new URLSearchParams({ mint, page: '1' })
    const res = await fetchJson(`/api/coin/trades?${qs.toString()}`)
    if (historyMint.value !== mint) return
    history.value = Array.isArray(res.trades) ? res.trades.slice().reverse() : []
  } catch {
    if (historyMint.value === mint) history.value = []
  }
}
</script>

<style scoped>
.chart{
  position:relative;
  height:380px;
  min-height:260px;
  border:1px solid #222;
  border-radius:8px;
  overflow:hidden;
  background:#0b0b0b;
}
canvas{ display:block; width:100%; height:100%; }
.empty{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  color:#555;
  font-size:13px;
  pointer-events:none;
}

@media (max-width: 640px) {
  .chart{
    height:250px;
    min-height:220px;
  }
}
</style>
